import * as amplitude from "@amplitude/analytics-browser";
import { plugin as engagementPlugin } from "@amplitude/engagement-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { useEffect } from "react";
import useAuth from "src/hooks/useAuth";

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;
const IS_DEV = import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === "development";

export const useAmplitude = (): void => {
    const { user, userAdminId } = useAuth();
    const isSuperAdmin = userAdminId !== undefined;
    useEffect(() => {
        const amplitudeUserId = isSuperAdmin ? `super-admin-${userAdminId}` : user?.email;

        if (amplitudeUserId === undefined) {
            return;
        }

        // In dev mode, skip Amplitude initialization and log to console instead
        if (IS_DEV) {
            if (AMPLITUDE_API_KEY === undefined || AMPLITUDE_API_KEY === "") {
                // biome-ignore lint/suspicious/noConsole: dev debug logging
                console.log(
                    "[Amplitude] Dev mode: tracking disabled (no API key). Events will be logged to console."
                );
            }
            return;
        }

        // Production: requires API key
        if (AMPLITUDE_API_KEY === undefined || AMPLITUDE_API_KEY === "") {
            // biome-ignore lint/suspicious/noConsole: dev debug logging
            console.warn("[Amplitude] Production mode: API key not configured. Tracking disabled.");
            return;
        }

        // Add Engagement plugin before init so it can initialize correctly
        amplitude.add(engagementPlugin());

        const sessionReplayTracking = sessionReplayPlugin({
            sampleRate: 1, // Capture 100% of sessions (adjust as needed for performance)
        });
        amplitude.add(sessionReplayTracking);

        // Initialize Amplitude for production
        // Don't activate amplitude for superadmin to avoid skewing data
        amplitude.init(AMPLITUDE_API_KEY, amplitudeUserId, {
            // cSpell:disable-next-line
            autocapture: {
                attribution: true, // Suivi des sources de trafic
                pageViews: true, // Suivi des pages visitées
                sessions: true, // Suivi des sessions
                formInteractions: !isSuperAdmin, // Désactive le suivi des formulaires
                fileDownloads: false, // Désactive le suivi des téléchargements
                elementInteractions: !isSuperAdmin, // Désactive le suivi des clics sur les éléments
            },
        });
    }, [user, userAdminId, isSuperAdmin]);
};
